/**
 * Byte transport for the asset pipeline.
 *
 * Streaming is what makes loading UI honest (CLAUDE.md rule 7): progress is
 * reported per received chunk, from real bytes on the wire.
 */

export interface FetchOptions {
  readonly signal?: AbortSignal;
  /**
   * The browser's Fetch Priority: "low" lets a background download (a large
   * environment) yield bandwidth to what the page is waiting for.
   */
  readonly priority?: RequestPriority;
  /** Called after each chunk. `total` is null when the server didn't send Content-Length. */
  readonly onChunk?: (loaded: number, total: number | null) => void;
}

export interface Fetcher {
  fetch(url: string, options?: FetchOptions): Promise<Uint8Array>;
}

/** The browser Fetcher: `fetch()` with the body read as a stream. */
export class HttpFetcher implements Fetcher {
  async fetch(url: string, options: FetchOptions = {}): Promise<Uint8Array> {
    const response = await fetch(url, {
      ...(options.signal && { signal: options.signal }),
      ...(options.priority && { priority: options.priority }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);

    const total = parseContentLength(response.headers.get("content-length"));
    if (!response.body) {
      const bytes = new Uint8Array(await response.arrayBuffer());
      options.onChunk?.(bytes.byteLength, total ?? bytes.byteLength);
      return bytes;
    }

    return readStream(response.body, total, options.onChunk);
  }
}

/** Reads a body stream to completion, reporting progress after every chunk. */
async function readStream(
  body: ReadableStream<Uint8Array>,
  total: number | null,
  onChunk: FetchOptions["onChunk"],
): Promise<Uint8Array> {
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onChunk?.(loaded, total);
  }

  return concatChunks(chunks, loaded);
}

export function concatChunks(chunks: readonly Uint8Array[], length: number): Uint8Array {
  const out = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}

/**
 * Content-Length is the *encoded* size. Under gzip/brotli it doesn't match the
 * bytes the stream yields, so callers fall back to the size from the content
 * index when the header is missing.
 */
function parseContentLength(header: string | null): number | null {
  if (header === null) return null;
  const value = Number(header);
  return Number.isFinite(value) && value >= 0 ? value : null;
}
